import { useEffect, useState, useMemo } from "react";
import Button from "../../design/Button";
import GModal from "../../design/components/GModal";
import { JService } from "../../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import { Autocomplete, ListItem, TextField, Box, CircularProgress } from "@mui/material";
import { SelectedUsersContainer } from "../../users-and-teams/components";
import { topNavAccountSelectedAccountTickIcon } from "../../../utility-functions/assetLoader";
import AnimationPage from "../../shared/animation-page/AnimationPage";

const GongSettingsModal = ({ visible, closeModal }) => {

  //States
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [usersMapping, setUsersMapping] = useState({});
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [initialSelectedUserIds, setInitialSelectedUserIds] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  //Handlers
  const fetchGongUsers = async (url = "/api/v2/integrations/gong-user-toggle/", isMore = false, isSearch = false) => {
    if (isMore) setIsFetchingMore(true);
    else if (!isSearch) setIsLoading(true);

    try {
      const res = await JService.get(url);
      if (res?.success) {
        const results = res.data?.data?.results || [];
        const next = res.data?.data?.next;
        setNextPageUrl(next);

        setUsersMapping((prev) => {
          const newMapping = { ...prev };
          results?.forEach((user) => {
            newMapping[user.user_id] = user;
          });
          return newMapping;
        });

        if (isMore) {
          setSearchResults((prev) => [...prev, ...results]);
        } else {
          setSearchResults(results);
        }

        if (url === "/api/v2/integrations/gong-user-toggle/" && !isMore) {
          const enabledInThisPage = results?.filter(u => u.gong_enabled)?.map(u => u.user_id) || [];
          setSelectedUserIds((prev) => [...new Set([...prev, ...enabledInThisPage])]);
          setInitialSelectedUserIds((prev) => [...new Set([...prev, ...enabledInThisPage])]);
        }
      } else {
        throw new Error(res?.message || "Failed to fetch Gong users");
      }
    } catch (err) {
      console.error("Error fetching Gong users:", err);
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const enabled = selectedUserIds.filter(id => !initialSelectedUserIds.includes(id));
      const disabled = initialSelectedUserIds.filter(id => !selectedUserIds.includes(id));

      const payload = {
        user_ids_enabled: enabled,
        user_ids_disabled: disabled,
      };

      const res = await JService.post("/api/v2/integrations/gong-user-toggle/", payload);
      if (res?.success) {
        enqueueSnackbar(res.message || "Settings updated successfully", { variant: "success" });
        closeModal();
      } else {
        throw new Error(res?.message || "Failed to update settings");
      }
    } catch (err) {
      console.error("Error updating Gong settings:", err);
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveUser = (uid) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== uid));
  };

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      nextPageUrl &&
      !isFetchingMore
    ) {
      fetchGongUsers(nextPageUrl, true, !!searchTerm);
    }
  };

  //Effects
  useEffect(() => {
    if (!visible) {
      setUsersMapping({});
      setSelectedUserIds([]);
      setInitialSelectedUserIds([]);
      setNextPageUrl(null);
      setSearchTerm("");
      setSearchResults([]);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (!searchTerm) {
      fetchGongUsers("/api/v2/integrations/gong-user-toggle/", false, false);
      return;
    }

    const timeoutId = setTimeout(() => {
        const url = `/api/v2/integrations/gong-user-toggle/?search=${encodeURIComponent(searchTerm)}`;
        fetchGongUsers(url, false, true);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, visible]);

  const hasChanges = useMemo(() => {
    if (selectedUserIds.length !== initialSelectedUserIds.length) return true;
    const initialSet = new Set(initialSelectedUserIds);
    return !selectedUserIds.every(id => initialSet.has(id));
  }, [selectedUserIds, initialSelectedUserIds]);

  return (
    <GModal
      visible={visible}
      heading="Gong integration settings"
      closeModal={closeModal}
      bottomButtons={[
        <Button
          key="cancel"
          buttonText="Cancel"
          variant="outlined"
          hierarchy="white"
          onClick={closeModal}
          styleOverride={{
            fontWeight: 500,
            height: "36px",
            width: "75px",
          }}
        />,
        <Button
          key="save"
          buttonText="Save"
          variant="contained"
          hierarchy="green"
          isLoading={isSaving}
          disabled={!hasChanges || isSaving}
          onClick={handleSave}
          styleOverride={{
            fontWeight: 500,
            height: "36px",
            width: "61px",
            opacity: (!hasChanges || isSaving) ? 0.5 : 1,
          }}
        />,
      ]}
      body={
        isLoading ? (
          <AnimationPage />
        ) : (
          <div>
            <p className="t7 medium-font nc-gray-900" style={{ marginBottom: "12px" }}>
              Users with access ({selectedUserIds.length})
            </p>
            <Autocomplete
              forcePopupIcon={false}
              multiple
              disableCloseOnSelect
              disableClearable
              sx={{
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #34CB65 !important",
                },
              }}
              size="small"
              ListboxProps={{ 
                sx: { maxHeight: '206px' },
                onScroll: handleScroll
              }}
              options={searchResults || []}
              getOptionLabel={(option) => `${option.first_name || ""} ${option.last_name || ""} ${option.email || ""}`}
              value={selectedUserIds.map(id => usersMapping[id]).filter(Boolean)}
              filterOptions={(x) => x}
              inputValue={searchTerm}
              onInputChange={(e, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setSearchTerm(value);
                }
              }}
              onChange={(e, values) => {
                setSelectedUserIds(values.map(v => v.user_id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter user name to add"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetchingMore ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={() => ""}
              renderOption={(props, option) => {
                const selected = selectedUserIds.includes(option.user_id);
                return (
                  <ListItem
                    {...props}
                    key={option.user_id}
                    sx={{
                      backgroundColor: selected ? "#F0FCF4 !important" : "white",
                      borderRadius: "5px",
                      "&:hover": { backgroundColor: "#F0FCF4 !important" },
                    }}
                  >
                    <Box sx={{ flexGrow: 1, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <p className="t7 regular-font nc-gray-900">{(option.first_name || "") + ' ' + (option.last_name || "")}</p>
                        <p className="t7 regular-font nc-gray-500" style={{ marginTop: 0 }}>{option.email}</p>
                      </div>
                      <span>{selected && topNavAccountSelectedAccountTickIcon()}</span>
                    </Box>
                  </ListItem>
                )
              }}
            />
            <Box
              className="custom-scrollbar"
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                marginTop: '12px',
                maxHeight: '250px',
                overflowY: 'auto',
                minHeight: '120px'
              }}
            >
              <SelectedUsersContainer
                selectedUsers={selectedUserIds}
                allUsers={usersMapping}
                handleRemoveUser={handleRemoveUser}
                noSelectionText="No users selected"
              />
            </Box>
          </div>
        )
      }
      modalStyle={{
        width: "440px",
      }}
    />
  );
};

export default GongSettingsModal;

